import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { OrgMemberService } from '@/api';
import type { OrgMemberResponse } from '@/api/generated/models/OrgMemberResponse';
import { handleAsyncThunkError } from './errorUtils';
import {
  createInitialPaginatedState,
  resetPaginatedData,
  updatePaginationMeta,
  type PaginatedState
} from './utils/paginationHelpers';

// Member state interface
type MemberState = PaginatedState<OrgMemberResponse>;

// Initial state
const initialState: MemberState = createInitialPaginatedState<OrgMemberResponse>(100);

// Async thunks
export const fetchMembers = createAsyncThunk(
  'member/fetchMembers',
  async (params: {
    orgId: string;
    workspaceId?: string;
    q?: string;
    page?: number;
    perPage?: number;
    includeUnavailable?: boolean;
    reset?: boolean; // 데이터 리셋 여부
  }, { rejectWithValue }) => {
    try {
      const response = await OrgMemberService.appsOrganizationViewsListOrgMembers(params);
      return {
        data: (response.data || []) as OrgMemberResponse[],
        meta: response.meta || { page: 1, per_page: 100, total: 0, pages: 1, has_next: false, has_prev: false },
        reset: params.reset || false,
        page: params.page || 1
      };
    } catch (error: unknown) {
      return handleAsyncThunkError(error, rejectWithValue, '멤버 목록 조회에 실패했습니다.');
    }
  }
);

// 추가 페이지 데이터를 가져오는 thunk
export const fetchMoreMembers = createAsyncThunk(
  'member/fetchMoreMembers',
  async (params: {
    orgId: string;
    workspaceId?: string;
    q?: string;
    page: number;
    perPage?: number;
    includeUnavailable?: boolean;
  }, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { member: MemberState };
      const { loadedPages } = state.member;

      // 이미 로드된 페이지인지 확인
      if (loadedPages.has(params.page)) {
        return null; // 이미 로드된 페이지는 건너뛰기
      }

      const response = await OrgMemberService.appsOrganizationViewsListOrgMembers(params);
      return {
        data: (response.data || []) as OrgMemberResponse[],
        meta: response.meta || { page: params.page, per_page: 100, total: 0, pages: 1, has_next: false, has_prev: false },
        page: params.page
      };
    } catch (error: unknown) {
      return handleAsyncThunkError(error, rejectWithValue, '추가 멤버 목록 조회에 실패했습니다.');
    }
  }
);

export const createMember = createAsyncThunk(
  'member/createMember',
  async (memberData: {
    org_id: string;
    account_id: string;
    account_name: string;
    account_email: string;
    account_phone?: string;
    role?: string
  }, { rejectWithValue }) => {
    try {
      const response = await OrgMemberService.appsOrganizationViewsCreateOrgMember({
        requestBody: memberData
      });
      return response;
    } catch (error: unknown) {
      return handleAsyncThunkError(error, rejectWithValue, '멤버 생성에 실패했습니다.');
    }
  }
);

export const updateMember = createAsyncThunk(
  'member/updateMember',
  async (params: {
    memberId: string;
    data: {
      account_name?: string;
      account_email?: string;
      account_phone?: string;
      role?: string;
      available?: boolean
    }
  }, { rejectWithValue }) => {
    try {
      const response = await OrgMemberService.appsOrganizationViewsUpdateOrgMember({
        memberId: params.memberId,
        requestBody: params.data
      });
      return response;
    } catch (error: unknown) {
      return handleAsyncThunkError(error, rejectWithValue, '멤버 수정에 실패했습니다.');
    }
  }
);

export const deleteMember = createAsyncThunk(
  'member/deleteMember',
  async (memberId: string, { rejectWithValue }) => {
    try {
      await OrgMemberService.appsOrganizationViewsDeleteOrgMember({ memberId });
      return memberId;
    } catch (error: unknown) {
      return handleAsyncThunkError(error, rejectWithValue, '멤버 삭제에 실패했습니다.');
    }
  }
);

// Member slice
const memberSlice = createSlice({
  name: 'member',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    resetMemberData: (state) => {
      resetPaginatedData(state);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch members
      .addCase(fetchMembers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMembers.fulfilled, (state, action) => {
        state.isLoading = false;
        const { data, meta, reset, page } = action.payload;

        if (reset) {
          // Reset mode: replace all data
          state.items = data;
          state.loadedPages = new Set([page]);
        } else {
          // Append mode: add to existing data (remove duplicates)
          const existingIds = new Set(state.items.map((m: OrgMemberResponse) => m.id));
          const newMembers = data.filter((m: OrgMemberResponse) => !existingIds.has(m.id));
          state.items.push(...newMembers);
          state.loadedPages.add(page);
        }

        updatePaginationMeta(state, meta);
        state.error = null;
      })
      .addCase(fetchMembers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch more members
      .addCase(fetchMoreMembers.pending, (state) => {
        state.isLoadingMore = true;
        state.error = null;
      })
      .addCase(fetchMoreMembers.fulfilled, (state, action) => {
        state.isLoadingMore = false;

        if (action.payload) {
          const { data, meta, page } = action.payload;

          // Add to existing data (remove duplicates)
          const existingIds = new Set(state.items.map((m: OrgMemberResponse) => m.id));
          const newMembers = data.filter((m: OrgMemberResponse) => !existingIds.has(m.id));
          state.items.push(...newMembers);
          state.loadedPages.add(page);

          // Update pagination meta
          updatePaginationMeta(state, meta);
        }

        state.error = null;
      })
      .addCase(fetchMoreMembers.rejected, (state, action) => {
        state.isLoadingMore = false;
        state.error = action.payload as string;
      })
      // Create member
      .addCase(createMember.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
      })
      .addCase(createMember.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Update member
      .addCase(updateMember.fulfilled, (state, action) => {
        const index = state.items.findIndex(m => m.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateMember.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Delete member
      .addCase(deleteMember.fulfilled, (state, action) => {
        state.items = state.items.filter(m => m.id !== action.payload);
      })
      .addCase(deleteMember.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setCurrentPage, setPageSize, resetMemberData } = memberSlice.actions;
export default memberSlice.reducer;